import React, { useEffect, useState } from 'react';

type OfficialAccount = {
    userId: string;
    handle: string;
    displayName: string;
    email: string;
    avatarUrl?: string;
};

type Props = {
    API_BASE: string;
    authHeaders: () => Record<string, string>;
};

export const OfficialAccountsSection: React.FC<Props> = ({ API_BASE, authHeaders }) => {
    const [accounts, setAccounts] = useState<OfficialAccount[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchUserId, setSearchUserId] = useState('');
    const [actionStatus, setActionStatus] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchAccounts = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${API_BASE}/admin/official-accounts`, {
                headers: authHeaders(),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to fetch official accounts.');
            }
            const data = await res.json();
            setAccounts(Array.isArray(data.accounts) ? data.accounts : []);
        } catch (err: any) {
            setError(err.message || 'Error retrieving official accounts.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const handleElevate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchUserId.trim()) return;
        setActionLoading(true);
        setActionStatus(null);
        try {
            const res = await fetch(`${API_BASE}/admin/set-official`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ userId: searchUserId.trim(), isOfficial: true }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to elevate account.');
            }
            setActionStatus('Account elevated to official status.');
            setSearchUserId('');
            fetchAccounts();
        } catch (err: any) {
            setActionStatus(`Error: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevoke = async (userId: string) => {
        setActionLoading(true);
        setActionStatus(null);
        try {
            const res = await fetch(`${API_BASE}/admin/set-official`, {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ userId, isOfficial: false }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err?.message || 'Failed to revoke official status.');
            }
            setActionStatus('Official status revoked.');
            fetchAccounts();
        } catch (err: any) {
            setActionStatus(`Error: ${err.message}`);
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div>
            <h2 style={{ marginBottom: '1rem' }}>Official Accounts</h2>
            <p style={{ marginBottom: '1rem', color: '#666', fontSize: '0.9rem' }}>
                Official accounts are auto-connected to every new user on signup.
            </p>

            <form onSubmit={handleElevate} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
                <input
                    type="text"
                    placeholder="User ID to elevate"
                    value={searchUserId}
                    onChange={e => setSearchUserId(e.target.value)}
                    style={{ flex: 1, padding: '0.5rem', borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#222' }}
                />
                <button
                    type="submit"
                    disabled={actionLoading || !searchUserId.trim()}
                    style={{ padding: '0.5rem 1rem', borderRadius: 4, background: '#4f8ef7', color: '#fff', border: 'none', cursor: 'pointer' }}
                >
                    {actionLoading ? '...' : 'Elevate'}
                </button>
            </form>

            {actionStatus && (
                <p style={{ marginBottom: '1rem', color: actionStatus.startsWith('Error') ? '#c0392b' : '#27ae60' }}>
                    {actionStatus}
                </p>
            )}

            {loading && <p>Loading...</p>}
            {error && <p style={{ color: '#c0392b' }}>{error}</p>}

            {!loading && !error && accounts.length === 0 && (
                <p style={{ color: '#888' }}>No official accounts yet.</p>
            )}

            {accounts.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: '#444' }}>Handle</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: '#444' }}>Display Name</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: '#444' }}>Email</th>
                            <th style={{ textAlign: 'left', padding: '0.5rem', color: '#444' }}>User ID</th>
                            <th style={{ padding: '0.5rem' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {accounts.map(account => (
                            <tr key={account.userId} style={{ borderBottom: '1px solid #eee' }}>
                                <td style={{ padding: '0.5rem' }}>@{account.handle}</td>
                                <td style={{ padding: '0.5rem' }}>{account.displayName}</td>
                                <td style={{ padding: '0.5rem' }}>{account.email}</td>
                                <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.8rem', color: '#888' }}>{account.userId}</td>
                                <td style={{ padding: '0.5rem' }}>
                                    <button
                                        onClick={() => handleRevoke(account.userId)}
                                        disabled={actionLoading}
                                        style={{ padding: '0.25rem 0.75rem', borderRadius: 4, background: '#e74c3c', color: '#fff', border: 'none', cursor: 'pointer' }}
                                    >
                                        Revoke
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};
